import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface TestimonialProps {
  content: string;
  author: string;
  position: string;
  image: string;
}

const testimonials: TestimonialProps[] = [
  {
    content: "Wealth Wave transformed my approach to investing. Their strategic guidance has increased my portfolio value by 27% in just one year.",
    author: "Rahul Mehta",
    position: "Tech Entrepreneur",
    image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
  },
  {
    content: "The team's expertise in tax-efficient investing has saved me lakhs while optimizing my returns. I couldn't be happier with their service.",
    author: "Priya Singh",
    position: "Healthcare Professional",
    image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
  },
  {
    content: "I've worked with several wealth management firms, but Wealth Wave is truly exceptional. Their personalized approach and attention to detail sets them apart.",
    author: "Vikram Kapoor",
    position: "Retired Banking Executive",
    image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
  }
];

const Testimonials: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const next = () => {
    setCurrent((current + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrent((current - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="section-title">Client Success Stories</h2>
          <p className="section-subtitle">
            Hear directly from our clients about how our expertise has helped them achieve 
            their financial goals and secure their future.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <motion.div
            key={current}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-6 md:p-8 lg:p-12 rounded-2xl md:rounded-3xl shadow-lg border border-gray-100"
          >
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 lg:gap-12 items-center">
              <div className="relative w-20 h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 flex-shrink-0">
                <img 
                  src={testimonials[current].image} 
                  alt={testimonials[current].author} 
                  className="w-full h-full object-cover rounded-xl md:rounded-2xl shadow-lg md:shadow-xl"
                />
                <div className="absolute inset-0 ring-1 ring-black/10 rounded-xl md:rounded-2xl"></div>
              </div>
              <div className="flex-1">
                <blockquote className="text-base md:text-lg lg:text-xl font-medium tracking-tight mb-4 md:mb-6 leading-relaxed text-center md:text-left">
                  "{testimonials[current].content}"
                </blockquote>
                <div className="text-center md:text-left">
                  <p className="font-semibold text-base md:text-lg mb-1">{testimonials[current].author}</p>
                  <p className="text-gray-500 font-medium text-sm md:text-base">{testimonials[current].position}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <button 
            onClick={prev} 
            className="absolute top-1/2 -left-3 md:-left-4 lg:-left-8 transform -translate-y-1/2 bg-black text-white p-2 md:p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>

          <button 
            onClick={next} 
            className="absolute top-1/2 -right-3 md:-right-4 lg:-right-8 transform -translate-y-1/2 bg-black text-white p-2 md:p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300"
            aria-label="Next testimonial"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>

          <div className="flex justify-center space-x-2 mt-6 md:mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrent(index)}
                className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${
                  index === current ? 'bg-black' : 'bg-gray-200'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;